import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as Avatar from '@givebutter/primitives/avatar';
import { Dot } from '@givebutter/primitives/dot';
import * as Meter from '@givebutter/primitives/meter';
import { faLock, faTablePicnic } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchSeatingGroups } from 'api/events';
import * as DropdownMenu from 'components/dropdown-menu';
import { TableConstants, sortQuery, useReactTableState } from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip';
import { TruncatedText } from 'components/truncated-text';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useCurrentCampaignEvent } from 'customHooks/useCurrentCampaignEvent';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { eventQueryKeys } from 'queries/event-query-keys';
import { twMerge } from 'tailwind-merge';
import { SeatingGroup } from 'types/domain';

const columnHelper = createColumnHelper<SeatingGroup>();
const AVATAR_TABLE_LIMIT = 10;
const TABLE_SEATING_PAGE_SIZE = 20;

const useTableSeatingIndex = () => {
    const history = useHistory();
    const { data: event } = useCurrentCampaignEvent();
    const { pagination, setPagination, sorting, setSorting } = useReactTableState({
        pageSize: TABLE_SEATING_PAGE_SIZE,
    });
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign.id;

    const [search, setSearch] = usePersistentSearch();
    const searchTerm = useDebounce(search);

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            per_page: pagination.pageSize,
            query: searchTerm ?? '',
            ...sortQuery(sorting),
        },
        500,
    );

    const { data: seatingGroup } = useSuspenseQuery({
        queryKey: eventQueryKeys.seatingGroupsWithFilters(event.id, params),
        queryFn: () => fetchSeatingGroups(event.id, params),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('number', {
                header: '#',
                cell: info => {
                    return <span>{info.getValue()}</span>;
                },
            }),
            columnHelper.accessor('name', {
                header: 'Table name',
                cell: info => {
                    return (
                        <div className={'relative flex items-center gap-2 font-medium'}>
                            <div>
                                <div className="flex max-w-[15rem]">
                                    <TruncatedText>{info.getValue()}</TruncatedText>
                                </div>
                            </div>
                            {info.row.original.locked === true && (
                                <div className={'flex items-center'}>
                                    <FontAwesomeIcon
                                        icon={faLock}
                                        className={'text-gray-500'}
                                        aria-label="Locked"
                                    />
                                </div>
                            )}
                        </div>
                    );
                },
            }),

            columnHelper.accessor('capacity', {
                header: 'Seats filled',
                cell: info => {
                    const filled = info.row.original.tickets?.length || 0;
                    const capacity = parseInt(info.getValue()) || 0;
                    const filledPercentage =
                        capacity > 0 ? Math.min(Math.round((filled / capacity) * 100), 100) : 0;

                    return (
                        <div className={'flex items-center gap-x-2 font-medium'}>
                            <div>
                                <span>{filled}</span>
                                <span>/</span>
                                <span>{capacity}</span>
                            </div>

                            <Meter.Root
                                type="line"
                                value={filledPercentage}
                                aria-label="Seats filled Percentage"
                                size="md"
                                variant="info"
                                className="w-20 flex-shrink-0"
                            >
                                <Meter.LineTrack className="bg-gray-200">
                                    <Meter.LineIndicator />
                                </Meter.LineTrack>
                            </Meter.Root>
                        </div>
                    );
                },
            }),
            columnHelper.display({
                header: 'Attendees',
                enableSorting: false,
                cell: info => {
                    const item = info.row.original;
                    return (
                        <div className={'flex items-center'}>
                            {item.tickets?.slice(0, AVATAR_TABLE_LIMIT).map(ticket => (
                                <Tooltip.Provider key={ticket.id} delayDuration={0}>
                                    <Tooltip.Root>
                                        <Tooltip.Trigger className="z-10 -ml-1 hover:z-20">
                                            <Avatar.Root
                                                size="2xs"
                                                className={twMerge(
                                                    'border bg-gray-100',
                                                    ticket.checked_in_at
                                                        ? 'border-green-500'
                                                        : 'border-gray-300',
                                                )}
                                            >
                                                <Avatar.Fallback
                                                    aria-label={`${ticket.first_name} ${ticket.last_name}`}
                                                >
                                                    <span className="font-medium tracking-tighter">
                                                        {ticket.first_name?.[0]}{' '}
                                                        {ticket.last_name?.[0]}
                                                    </span>
                                                </Avatar.Fallback>
                                            </Avatar.Root>
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                            <Tooltip.Content inverse side="top">
                                                <div className="flex items-center gap-2 whitespace-nowrap text-xs">
                                                    <p className="font-medium text-white">
                                                        {ticket.first_name} {ticket.last_name}
                                                    </p>

                                                    <div className="flex items-center gap-1 rounded-full bg-gray-800 px-2 py-1">
                                                        <Dot
                                                            className={twMerge(
                                                                ticket.checked_in_at
                                                                    ? 'bg-green-500'
                                                                    : 'bg-gray-500',
                                                            )}
                                                        />

                                                        <span className="text-gray-100">
                                                            {ticket.checked_in_at
                                                                ? 'Checked-in'
                                                                : 'Not checked-in'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <Tooltip.Arrow inverse />
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                </Tooltip.Provider>
                            ))}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: TableConstants.Actions,
                cell: info => (
                    <DropdownMenu.Root>
                        <DropdownMenu.EllipsisTrigger className="float-right px-2 py-1" />
                        <DropdownMenu.Content align={'end'}>
                            <DropdownMenu.Item
                                onClick={() => {
                                    history.push(
                                        `/accounts/${accountId}/campaigns/${campaignId}/event/table-seating/${info.row.original.id}`,
                                    );
                                }}
                            >
                                <div className={'flex items-center gap-x-1.5'}>
                                    <FontAwesomeIcon
                                        className={'flex size-4'}
                                        icon={faTablePicnic}
                                    />
                                    <p>View table details</p>
                                </div>
                            </DropdownMenu.Item>
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                ),
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        [history, accountId, campaignId],
    );

    const table = useReactTable({
        data: seatingGroup.data,
        rowCount: seatingGroup.meta.total,
        columns,
        pageCount: seatingGroup.meta.last_page ?? -1,

        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        manualPagination: true,
        manualSorting: true,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            to: cell =>
                `/accounts/${accountId}/campaigns/${campaignId}/event/table-seating/${cell.row.original.id}`,
        },
    });

    useSyncPaginatedData(table, seatingGroup);

    return {
        campaignId,
        table,
        search,
        setSearch,
        searchTerm,
        isPending,
    };
};

export { useTableSeatingIndex };
